import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { intelligenceAPI, SupplierQualification, QualificationTemplate, Company, coreAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Pagination } from '../components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ClipboardCheck,
  Plus,
  Search,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Send,
  Circle,
  LayoutGrid,
  List,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useIsCustomer } from '../hooks/usePortalMode';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

export default function SupplierQualifications() {
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const { isMenuItemVisible, isComplianceMode } = useMenuSettings();
  const { user } = useAuth();
  // NCRs tab is irrelevant when Quality is disabled for the tenant, and
  // makes no sense on a compliance-only tenant (MAB) where Quality is
  // hidden platform-wide. Gate it so the cross-nav only appears when the
  // module is actually in use.
  const showNcrLink = isMenuItemVisible('quality') && !isComplianceMode;
  const [qualifications, setQualifications] = useState<SupplierQualification[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'matrix'>('list');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Pagination state for supplier rows
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // Assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [templates, setTemplates] = useState<QualificationTemplate[]>([]);
  const [suppliers, setSuppliers] = useState<Company[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadQualifications();
  }, [filterStatus, isCustomer]);

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to page 1 on search
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    // Load templates for matrix view column headers
    loadTemplates();
    if (showAssignDialog && isCustomer) {
      loadSuppliers();
    }
  }, [showAssignDialog]);

  const loadQualifications = async () => {
    try {
      setLoading(true);
      // Fetch ALL qualifications by paginating through all pages
      const allQualifications: SupplierQualification[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const params: Record<string, string | number | boolean> = { page_size: pageSize, page };
        if (filterStatus !== 'all') params['status'] = filterStatus;

        const response = await intelligenceAPI.getQualifications(params);
        const results = response.data.results || [];
        allQualifications.push(...results);

        // Check if we've fetched all items
        if (allQualifications.length >= response.data.count || results.length < pageSize) {
          break;
        }
        page++;
      }

      setQualifications(allQualifications);
    } catch (err: unknown) {
      console.error('Error loading qualifications:', err);
      toast.error('Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // Fetch ALL templates
      const allTemplates: QualificationTemplate[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const response = await intelligenceAPI.getTemplates({ page_size: pageSize, page });
        const results = response.data.results || [];
        allTemplates.push(...results);

        if (allTemplates.length >= response.data.count || results.length < pageSize) {
          break;
        }
        page++;
      }

      setTemplates(allTemplates);
    } catch (err: unknown) {
      console.error('Error loading templates:', err);
    }
  };

  const loadSuppliers = async () => {
    try {
      // Fetch ALL suppliers
      const allSuppliers: Company[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const response = await coreAPI.getCompanies({ company_type: 'supplier', page_size: pageSize, page });
        const results = response.data.results || [];
        allSuppliers.push(...results);

        // Companies API might not have count, so just check if we got fewer results
        if (results.length < pageSize) {
          break;
        }
        page++;
      }

      setSuppliers(allSuppliers);
    } catch (err: unknown) {
      console.error('Error loading suppliers:', err);
    }
  };

  const handleAssignQualification = async () => {
    if (!selectedTemplate || !selectedSupplier) {
      toast.error('Please select both template and supplier');
      return;
    }

    try {
      setAssigning(true);
      const response = await intelligenceAPI.createQualification({
        template: selectedTemplate,
        supplier: selectedSupplier,
        customer: user?.company,
        status: 'invited',
        notes: assignNotes,
      });

      // Send invitation
      await intelligenceAPI.inviteSupplier(response.data.id);

      toast.success('Qualification assigned and invitation sent');
      setShowAssignDialog(false);
      setSelectedTemplate(null);
      setSelectedSupplier(null);
      setAssignNotes('');
      loadQualifications();
    } catch (err: unknown) {
      console.error('Error assigning qualification:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign qualification';
      toast.error(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  // Status configuration with colors matching the old portal
  const statusConfig: Record<string, { icon: LucideIcon; color: string; bgColor: string; dotColor: string; label: string }> = {
    invited: { icon: Send, color: 'text-blue-700', bgColor: 'bg-blue-100', dotColor: '#3B82F6', label: 'Invited' },
    in_progress: { icon: Clock, color: 'text-yellow-700', bgColor: 'bg-yellow-100', dotColor: '#EAB308', label: 'In Progress' },
    submitted: { icon: AlertCircle, color: 'text-orange-700', bgColor: 'bg-orange-100', dotColor: '#F97316', label: 'Submitted' },
    under_review: { icon: Clock, color: 'text-purple-700', bgColor: 'bg-purple-100', dotColor: '#A855F7', label: 'Under Review' },
    approved: { icon: CheckCircle2, color: 'text-green-700', bgColor: 'bg-green-100', dotColor: '#22C55E', label: 'Approved' },
    rejected: { icon: XCircle, color: 'text-red-700', bgColor: 'bg-red-100', dotColor: '#EF4444', label: 'Rejected' },
    more_info_required: { icon: AlertCircle, color: 'text-amber-700', bgColor: 'bg-amber-100', dotColor: '#F59E0B', label: 'More Info' },
    expired: { icon: XCircle, color: 'text-gray-700', bgColor: 'bg-gray-100', dotColor: '#6B7280', label: 'Expired' },
  };

  // Matrix cell with colored circle (matching old portal style)
  const MatrixCell = ({ qual }: { qual: SupplierQualification | undefined }) => {
    if (!qual) {
      return <span className="text-muted-foreground">—</span>;
    }

    const config = statusConfig[qual.status] ?? { dotColor: '#6B7280', label: qual.status };
    const expiryDate = qual.expiry_date ? new Date(qual.expiry_date).toLocaleDateString() : null;
    const score = qual.score_percentage ? parseFloat(String(qual.score_percentage)).toFixed(0) + '%' : null;

    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              onClick={() => navigate(`/intelligence/qualifications/${qual.id}`)}
              className="p-1 rounded-full hover:bg-muted/50 transition-colors"
            >
              <Circle
                className="h-6 w-6"
                fill={config.dotColor}
                stroke={config.dotColor}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1 text-sm">
              <div className="font-semibold">{config.label}</div>
              {expiryDate && (
                <div className="text-muted-foreground">
                  Expiry: {expiryDate}
                </div>
              )}
              {score && (
                <div className="text-muted-foreground">
                  Score: {score}
                </div>
              )}
              <div className="text-xs text-muted-foreground italic">
                Click to view details
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const filteredQualifications = qualifications.filter(qual =>
    qual.template_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    qual.supplier_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    qual.customer_name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Build matrix data: group qualifications by supplier
  const matrixData = useMemo(() => {
    const supplierMap = new Map<number, {
      id: number;
      name: string;
      qualifications: Map<number, SupplierQualification>;
    }>();

    filteredQualifications.forEach(qual => {
      const supplierId = qual.supplier;
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          id: supplierId,
          name: qual.supplier_name || `Supplier ${supplierId}`,
          qualifications: new Map(),
        });
      }
      supplierMap.get(supplierId)!.qualifications.set(qual.template, qual);
    });

    return Array.from(supplierMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredQualifications]);

  // Get unique templates from qualifications for matrix columns
  const matrixTemplates = useMemo(() => {
    const templateMap = new Map<number, { id: number; name: string }>();
    filteredQualifications.forEach(qual => {
      if (!templateMap.has(qual.template)) {
        templateMap.set(qual.template, {
          id: qual.template,
          name: qual.template_name || `Template ${qual.template}`,
        });
      }
    });
    return Array.from(templateMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredQualifications]);

  // Pagination calculations for supplier rows
  const totalSuppliers = matrixData.length;
  const totalPages = Math.ceil(totalSuppliers / pageSize);
  const paginatedMatrixData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return matrixData.slice(startIndex, startIndex + pageSize);
  }, [matrixData, currentPage, pageSize]);

  // Reset to page 1 when status filter changes (search reset is handled in debounce effect)
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading qualifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Main Card Container - matches Quality page layout */}
      <Card className="rounded-lg shadow-sm">
        {/* Navigation Tabs Header */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-2">
              {showNcrLink && (
                <Link
                  to="/quality"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  NCRs
                </Link>
              )}
              {isCustomer && (
                <Link
                  to="/intelligence/qualifications"
                  className="px-4 py-2 text-sm font-medium rounded-md border-2 border-primary bg-primary text-primary-foreground shadow-sm"
                >
                  Approval Register
                </Link>
              )}
            </nav>
            {isCustomer && (
              <Button onClick={() => setShowAssignDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Assign Qualification
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Filters Toolbar */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search qualifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="invited">Invited</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="more_info_required">More Info Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-1 border rounded-md p-1 bg-muted/30">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-8 px-3"
                >
                  <List className="h-4 w-4 mr-1" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'matrix' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('matrix')}
                  className="h-8 px-3"
                >
                  <LayoutGrid className="h-4 w-4 mr-1" />
                  Matrix
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {filteredQualifications.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No qualifications found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : isCustomer
                    ? 'Get started by assigning a qualification to a supplier'
                    : 'No qualifications have been assigned to you yet'}
                </p>
                {isCustomer && !searchTerm && filterStatus === 'all' && (
                  <Button onClick={() => setShowAssignDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Assign Qualification
                  </Button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              /* ============ LIST VIEW ============ */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Supplier Responses ({filteredQualifications.length})
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Track supplier questionnaire completion and scores
                    </p>
                  </div>
                </div>

                {/* List View Table */}
                <div className="overflow-x-auto border rounded-lg shadow-sm">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-gray-300 bg-muted/50">
                        <th className="p-3 text-left font-semibold text-sm">Supplier</th>
                        <th className="p-3 text-left font-semibold text-sm">Questionnaire</th>
                        <th className="p-3 text-left font-semibold text-sm">Status</th>
                        <th className="p-3 text-center font-semibold text-sm">Completion</th>
                        <th className="p-3 text-center font-semibold text-sm">Score</th>
                        <th className="p-3 text-left font-semibold text-sm">Date Sent</th>
                        <th className="p-3 text-left font-semibold text-sm">Expiry Date</th>
                        <th className="p-3 text-center font-semibold text-sm">Risk</th>
                        <th className="p-3 text-center font-semibold text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQualifications.map((qual, idx) => {
                        const statusConf = statusConfig[qual.status] || statusConfig['invited'];
                        const isOverdue = qual.expiry_date && new Date(qual.expiry_date) < new Date() && qual.status !== 'approved' && qual.status !== 'rejected';
                        const riskLevel = qual.passed === false ? 'high' : qual.score_percentage && qual.score_percentage < 70 ? 'medium' : 'low';
                        return (
                          <tr
                            key={qual.id}
                            className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                          >
                            <td className="p-3">
                              <div className="font-medium text-sm">{qual.supplier_name}</div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm">{qual.template_name}</div>
                            </td>
                            <td className="p-3">
                              <Badge
                                className="text-xs"
                                style={{ backgroundColor: statusConf.dotColor, color: '#fff' }}
                              >
                                {statusConf.label}
                              </Badge>
                              {isOverdue && (
                                <Badge variant="destructive" className="ml-1 text-xs">
                                  Overdue
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: `${qual.completion_percentage || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {qual.completion_percentage || 0}%
                                </span>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              {qual.score_percentage !== undefined ? (
                                <span className={`text-sm font-medium ${qual.passed ? 'text-green-600' : qual.passed === false ? 'text-red-600' : ''}`}>
                                  {qual.score_percentage}%
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-muted-foreground">
                                {qual.invited_at ? new Date(qual.invited_at).toLocaleDateString() : '—'}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                {qual.expiry_date ? new Date(qual.expiry_date).toLocaleDateString() : '—'}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              {qual.status === 'approved' || qual.status === 'submitted' || qual.status === 'under_review' ? (
                                <Badge
                                  variant={riskLevel === 'high' ? 'destructive' : riskLevel === 'medium' ? 'secondary' : 'outline'}
                                  className="text-xs"
                                >
                                  {riskLevel === 'high' ? (
                                    <><AlertTriangle className="h-3 w-3 mr-1" />High</>
                                  ) : riskLevel === 'medium' ? (
                                    'Medium'
                                  ) : (
                                    'Low'
                                  )}
                                </Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/intelligence/qualifications/${qual.id}`)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* ============ MATRIX VIEW ============ */
              <div className="space-y-4">
                {/* Matrix Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Approval Register ({totalSuppliers} Suppliers)
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Hover over circles for details, click to view qualification
                    </p>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {matrixTemplates.length} Qualification Types
                  </span>
                </div>

                {/* Top Pagination */}
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalSuppliers}
                    itemsPerPage={pageSize}
                    onPageChange={setCurrentPage}
                    itemLabel="suppliers"
                  />
                )}

                {/* Matrix Table with horizontal scroll */}
                {/* Legend with colored circles - displayed above the table */}
                <div className="p-4 border rounded-lg bg-muted/20 mb-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <span className="text-sm font-medium text-muted-foreground">Legend:</span>
                    {Object.entries(statusConfig).map(([status, config]) => (
                      <div key={status} className="flex items-center gap-2 text-sm">
                        <Circle
                          className="h-4 w-4"
                          fill={config.dotColor}
                          stroke={config.dotColor}
                        />
                        <span>{config.label}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>—</span>
                      <span>Not Assigned</span>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto border rounded-lg shadow-sm">
                  <table className="w-full border-collapse" style={{ minWidth: `${250 + matrixTemplates.length * 130}px` }}>
                    <thead>
                      <tr className="border-b-2 border-gray-300 bg-muted/50">
                        <th className="p-3 text-left font-semibold text-sm sticky left-0 bg-muted/50 z-10 min-w-[250px] border-r border-gray-300">
                          Supplier
                        </th>
                        {matrixTemplates.map(template => (
                          <th
                            key={template.id}
                            className="p-3 text-center font-semibold text-sm min-w-[120px] max-w-[150px] border-l border-gray-200"
                            title={template.name}
                          >
                            <div className="text-xs leading-tight break-words whitespace-normal">
                              {template.name}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMatrixData.map((supplier, idx) => (
                        <tr
                          key={supplier.id}
                          className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                        >
                          <td className="p-3 sticky left-0 bg-inherit z-10 border-r border-gray-300">
                            <div className="font-medium text-sm">{supplier.name}</div>
                          </td>
                          {matrixTemplates.map(template => {
                            const qual = supplier.qualifications.get(template.id);
                            return (
                              <td key={template.id} className="p-3 text-center border-l border-gray-100">
                                <MatrixCell qual={qual} />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalSuppliers}
                    itemsPerPage={pageSize}
                    onPageChange={setCurrentPage}
                    itemLabel="suppliers"
                  />
                )}

              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assign Qualification Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Qualification to Supplier</DialogTitle>
            <DialogDescription>
              Select a template and supplier to create a new qualification
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template *</Label>
              <Select
                value={selectedTemplate?.toString() || ''}
                onValueChange={(value) => setSelectedTemplate(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      {template.name} ({template.template_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select
                value={selectedSupplier?.toString() || ''}
                onValueChange={(value) => setSelectedSupplier(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id.toString()}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="Add any notes or instructions for the supplier"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignQualification} disabled={assigning}>
              {assigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Assign & Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
